/* eslint-disable */
import { useState, useEffect, createContext, useContext, useCallback, useRef } from "react";

/* ═══════════════════════════════════════════════════════
   FOOTY ANALYST — AUTHENTICATION SYSTEM
   Real Supabase login · Username display · Session mgmt
   localStorage persistence for squad data
═══════════════════════════════════════════════════════ */

const AUTH_CSS = `
/* AUTH SCREEN */
.auth-wrap {
  min-height: 100vh;
  background: var(--teal2);
  display: flex; align-items: center; justify-content: center;
  padding: 2rem; position: relative; overflow: hidden;
}
.auth-wrap::before {
  content: '';
  position: absolute; inset: 0;
  background: repeating-linear-gradient(
    0deg, transparent, transparent 48px,
    rgba(0,0,0,0.06) 48px, rgba(0,0,0,0.06) 49px
  );
  pointer-events: none;
}
.auth-corner { position: absolute; width: 80px; height: 80px; border: 2px solid rgba(255,255,255,0.12); }
.auth-corner.tl { top: 24px; left: 24px; }
.auth-corner.tr { top: 24px; right: 24px; }
.auth-corner.bl { bottom: 24px; left: 24px; }
.auth-corner.br { bottom: 24px; right: 24px; }

.auth-card {
  background: var(--white);
  border: 2.5px solid var(--ink);
  padding: 2.8rem;
  width: 100%; max-width: 420px;
  position: relative; z-index: 1;
  box-shadow: 8px 8px 0 rgba(0,0,0,0.3);
}
.auth-card::before {
  content: '';
  position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px;
  border: 1px solid var(--rule);
  pointer-events: none;
}

/* Logo */
.auth-logo {
  display: flex; align-items: center; gap: 0.75rem;
  justify-content: center; margin-bottom: 0.4rem;
}
.auth-badge {
  width: 42px; height: 42px;
  background: var(--teal); border: 2.5px solid var(--ink);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Bebas Neue', sans-serif;
  font-size: 1.1rem; letter-spacing: 1px; color: var(--white);
  transform: rotate(-4deg);
  box-shadow: 2px 2px 0 rgba(0,0,0,0.15);
}
.auth-brand { font-family: 'Bebas Neue', sans-serif; font-size: 2rem; letter-spacing: 4px; color: var(--ink); }
.auth-tagline {
  font-family: 'DM Sans', sans-serif; font-size: 0.62rem; font-weight: 500;
  letter-spacing: 3px; text-transform: uppercase; color: var(--muted);
  text-align: center; margin-bottom: 2rem;
}

/* Tabs */
.auth-mode-tabs { display: flex; border: 2px solid var(--ink); margin-bottom: 1.6rem; }
.auth-mode-tab {
  flex: 1; padding: 0.6rem; text-align: center;
  font-family: 'DM Sans', sans-serif; font-size: 0.72rem; font-weight: 800;
  letter-spacing: 1.5px; text-transform: uppercase;
  cursor: pointer; color: var(--muted);
  border-right: 1px solid var(--rule); transition: all 0.2s;
}
.auth-mode-tab:last-child { border-right: none; }
.auth-mode-tab.on { background: var(--teal); color: var(--white); }

/* Role tabs */
.auth-role-label {
  font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 800;
  letter-spacing: 2.5px; text-transform: uppercase; color: var(--muted);
  margin-bottom: 0.5rem; display: block;
}
.auth-roles { display: flex; gap: 0.5rem; margin-bottom: 1.3rem; }
.auth-role-btn {
  flex: 1; padding: 0.6rem; text-align: center;
  font-family: 'DM Sans', sans-serif; font-size: 0.72rem; font-weight: 800;
  letter-spacing: 1px; text-transform: uppercase;
  cursor: pointer; border: 1.5px solid var(--rule);
  color: var(--muted); background: transparent; transition: all 0.2s;
}
.auth-role-btn:hover { border-color: var(--teal); color: var(--teal); }
.auth-role-btn.on { background: var(--teal); border-color: var(--teal); color: var(--white); }

/* Fields */
.auth-field { margin-bottom: 1.1rem; }
.auth-field label {
  display: block; font-family: 'DM Sans', sans-serif;
  font-size: 0.6rem; font-weight: 800; letter-spacing: 2px;
  text-transform: uppercase; color: var(--muted); margin-bottom: 0.38rem;
}
.auth-input {
  width: 100%; background: var(--cream);
  border: 1.5px solid var(--rule); border-bottom: 2.5px solid var(--ink);
  padding: 0.72rem 0.9rem; color: var(--ink);
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 500;
  outline: none; transition: border-color 0.2s;
}
.auth-input:focus { border-bottom-color: var(--teal); }
.auth-input.error { border-color: var(--darkred); border-bottom-color: var(--darkred); }

/* Error message */
.auth-error {
  background: var(--darkred3); border: 1.5px solid var(--darkred);
  border-left: 4px solid var(--darkred);
  padding: 0.65rem 0.9rem; margin-bottom: 1rem;
  font-family: 'DM Sans', sans-serif; font-size: 0.78rem;
  font-weight: 600; color: var(--darkred); line-height: 1.5;
}

/* Success message */
.auth-success {
  background: var(--teal4); border: 1.5px solid var(--teal);
  border-left: 4px solid var(--teal);
  padding: 0.65rem 0.9rem; margin-bottom: 1rem;
  font-family: 'DM Sans', sans-serif; font-size: 0.78rem;
  font-weight: 600; color: var(--teal2); line-height: 1.5;
}

/* Submit button */
.auth-submit {
  width: 100%; padding: 0.92rem;
  background: var(--teal); color: var(--white);
  border: 2px solid var(--teal);
  font-family: 'DM Sans', sans-serif; font-weight: 800;
  font-size: 0.82rem; letter-spacing: 2.5px; text-transform: uppercase;
  cursor: pointer; transition: all 0.2s; margin-top: 0.3rem;
  display: flex; align-items: center; justify-content: center; gap: 0.6rem;
}
.auth-submit:hover { background: var(--teal2); border-color: var(--teal2); }
.auth-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.auth-submit-spinner {
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: var(--white);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Switch mode */
.auth-switch {
  text-align: center; margin-top: 1.4rem;
  font-family: 'DM Sans', sans-serif; font-size: 0.76rem;
  color: var(--muted); font-weight: 400;
}
.auth-switch span { color: var(--teal); font-weight: 700; cursor: pointer; }
.auth-switch span:hover { text-decoration: underline; }

/* Password strength */
.auth-strength { margin-top: 0.35rem; }
.auth-strength-bar { height: 3px; border-radius: 2px; transition: all 0.3s; }
.auth-strength-label {
  font-family: 'DM Sans', sans-serif; font-size: 0.58rem;
  font-weight: 700; margin-top: 0.25rem; letter-spacing: 1px;
}

/* Demo accounts */
.auth-demo {
  margin-top: 1.2rem; padding: 0.85rem;
  background: var(--mustard3); border: 1px solid var(--mustard);
}
.auth-demo-title {
  font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 800;
  letter-spacing: 2px; text-transform: uppercase; color: var(--mustard2);
  margin-bottom: 0.55rem;
}
.auth-demo-row {
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.38rem 0; border-bottom: 1px solid rgba(212,148,26,0.2);
  cursor: pointer; transition: all 0.2s;
}
.auth-demo-row:last-child { border-bottom: none; }
.auth-demo-row:hover { opacity: 0.8; }
.auth-demo-role {
  font-family: 'DM Sans', sans-serif; font-size: 0.6rem; font-weight: 800;
  padding: 0.1rem 0.4rem; letter-spacing: 1.5px; text-transform: uppercase;
}
.auth-demo-email {
  font-family: 'DM Sans', sans-serif; font-size: 0.72rem;
  font-weight: 600; color: var(--ink); flex: 1;
}
.auth-demo-use {
  font-family: 'DM Sans', sans-serif; font-size: 0.6rem;
  color: var(--mustard2); font-weight: 700;
}

/* USER PILL in nav */
.user-pill {
  display: flex; align-items: center; gap: 0.5rem;
  background: var(--cream2);
  border: 1.5px solid var(--rule);
  padding: 0.28rem 0.7rem;
  cursor: pointer;
  transition: border-color 0.15s;
}
.user-pill:hover { border-color: var(--teal); }
.user-pill-av {
  width: 22px; height: 22px;
  background: var(--mustard); border: 1.5px solid var(--ink);
  display: flex; align-items: center; justify-content: center;
  font-family: 'DM Sans', sans-serif; font-size: 0.56rem; font-weight: 800;
  color: var(--ink); flex-shrink: 0;
}
.user-pill-name {
  font-family: 'DM Sans', sans-serif; font-size: 0.68rem;
  font-weight: 700; color: var(--ink); max-width: 90px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.user-pill-role {
  font-family: 'DM Sans', sans-serif; font-size: 0.55rem;
  color: var(--teal); font-weight: 700; letter-spacing: 1px;
  text-transform: uppercase;
}
`;

/* ── SUPABASE CLIENT ── */
import { createClient } from '@supabase/supabase-js';

let supabaseClient = null;

function getSupabase() {
  if (supabaseClient) return supabaseClient;
  const url = process.env.REACT_APP_SUPABASE_URL;
  const key = process.env.REACT_APP_SUPABASE_ANON_KEY;
  if (!url || !key || url.includes('yourproject')) return null;
  try {
    supabaseClient = createClient(url, key);
    return supabaseClient;
  } catch(e) {
    console.warn('Supabase init failed:', e.message);
    return null;
  }
}

// Export so SupabaseSync can use the same instance
export function getSupabaseClient() {
  return getSupabase();
}

/* ── AUTH CONTEXT ── */
export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

/* ── PASSWORD STRENGTH ── */
function getPasswordStrength(pw) {
  if (!pw) return { score: 0, label: '', color: 'transparent' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const levels = [
    { label: 'Too short', color: '#ef4444' },
    { label: 'Weak', color: '#f97316' },
    { label: 'Fair', color: '#eab308' },
    { label: 'Good', color: '#84cc16' },
    { label: 'Strong', color: '#22c55e' },
    { label: 'Very Strong', color: '#16a34a' },
  ];
  return { score, ...levels[Math.min(score, levels.length - 1)] };
}

/* ── DEMO ACCOUNTS ── */
const DEMO_ACCOUNTS = [
  { role: 'coach', email: 'coach@footyanalyst.co.uk', password: 'CoachTest123!', name: 'James Clarke', label: 'Coach' },
  { role: 'scout', email: 'scout@footyanalyst.co.uk', password: 'ScoutTest123!', name: 'Mike Hunter', label: 'Scout' },
];

/* ── LOCAL STORAGE HELPERS ── */
export function saveToStorage(key, data) {
  try {
    localStorage.setItem(`footyanalyst_${key}`, JSON.stringify(data));
    return true;
  } catch(e) {
    console.warn('Storage save failed:', e);
    return false;
  }
}

export function loadFromStorage(key, fallback = null) {
  try {
    const item = localStorage.getItem(`footyanalyst_${key}`);
    return item ? JSON.parse(item) : fallback;
  } catch(e) {
    return fallback;
  }
}

export function clearStorage(key) {
  try {
    localStorage.removeItem(`footyanalyst_${key}`);
  } catch(e) {}
}

/* ══════════════════════════════════════
   AUTH PROVIDER — wraps the whole app
══════════════════════════════════════ */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for saved session in localStorage
    const saved = loadFromStorage('session');
    if (saved) {
      setUser(saved);
    }

    // Also check Supabase session if configured
    const sb = getSupabase();
    if (sb) {
      sb.auth.getSession().then(({ data: { session } }) => {
        if (session?.user) {
          const profile = loadFromStorage(`profile_${session.user.id}`) || {};
          setUser({
            id: session.user.id,
            email: session.user.email,
            name: profile.name || session.user.email.split('@')[0],
            role: profile.role || 'coach',
            plan: profile.plan || 'pro',
            initials: (profile.name || session.user.email.split('@')[0]).slice(0,2).toUpperCase(),
          });
        }
      });

      const { data: { subscription } } = sb.auth.onAuthStateChange((_event, session) => {
        if (session?.user) {
          const profile = loadFromStorage(`profile_${session.user.id}`) || {};
          const u = {
            id: session.user.id,
            email: session.user.email,
            name: profile.name || session.user.email.split('@')[0],
            role: profile.role || 'coach',
            plan: profile.plan || 'pro',
            initials: (profile.name || session.user.email.split('@')[0]).slice(0,2).toUpperCase(),
          };
          setUser(u);
          saveToStorage('session', u);
        } else {
          setUser(null);
          clearStorage('session');
        }
      });

      setLoading(false);
      return () => subscription.unsubscribe();
    }

    setLoading(false);
  }, []);

  const signIn = async (email, password, role) => {
    const sb = getSupabase();

    // Try Supabase first
    if (sb) {
      const { data, error } = await sb.auth.signInWithPassword({ email, password });
      if (!error && data.user) {
        return { success: true };
      }
    }

    // Demo account fallback (works without Supabase configured)
    const demo = DEMO_ACCOUNTS.find(d => d.email === email && d.password === password);
    if (demo) {
      const u = {
        id: `demo_${demo.role}`,
        email: demo.email,
        name: demo.name,
        role: demo.role,
        plan: 'pro',
        initials: demo.name.split(' ').map(w=>w[0]).join(''),
        isDemo: true,
      };
      setUser(u);
      saveToStorage('session', u);
      return { success: true };
    }

    // Check locally saved accounts (for users who signed up without Supabase)
    const accounts = loadFromStorage('accounts', []);
    const account = accounts.find(a => a.email === email && a.password === password);
    if (account) {
      const u = { ...account, initials: account.name.split(' ').map(w=>w[0]).join('') };
      setUser(u);
      saveToStorage('session', u);
      return { success: true };
    }

    return { success: false, error: 'Invalid email or password. Check your details and try again.' };
  };

  const signUp = async (email, password, name, role) => {
    const sb = getSupabase();

    if (sb) {
      const { data, error } = await sb.auth.signUp({ email, password, options: { data: { full_name: name } } });
      if (!error && data.user) {
        const userId = data.user.id;
        // Save minimal profile — onboarding will fill the rest
        saveToStorage(`profile_${userId}`, { name, role, plan: 'basic', email });
        return { success: true, userId, message: 'Account created! Check your email to confirm your account.' };
      }
      if (error) return { success: false, error: error.message };
    }

    // Local account creation (fallback)
    const accounts = loadFromStorage('accounts', []);
    if (accounts.find(a => a.email === email)) {
      return { success: false, error: 'An account with this email already exists.' };
    }
    const newAccount = {
      id: `local_${Date.now()}`,
      email, password, name, role,
      plan: 'basic',
      createdAt: new Date().toISOString(),
    };
    accounts.push(newAccount);
    saveToStorage('accounts', accounts);

    const u = { ...newAccount, initials: name.split(' ').map(w=>w[0]).join('') };
    setUser(u);
    saveToStorage('session', u);
    // Save minimal profile — onboarding will fill the rest
    saveToStorage(`profile_${u.id}`, { name, role, plan: 'basic', email });
    return { success: true, userId: u.id, message: null }; // null message = local, skip email confirm
  };

  const signOut = async () => {
    const sb = getSupabase();
    if (sb) await sb.auth.signOut();
    setUser(null);
    clearStorage('session');
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

/* ══════════════════════════════════════
   LOGIN / SIGNUP SCREEN
══════════════════════════════════════ */
export function AuthScreen() {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState('login'); // login | signup | onboard | confirm
  const [role, setRole] = useState('coach');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [signedUpEmail, setSignedUpEmail] = useState('');
  const [signedUpUserId, setSignedUpUserId] = useState(null);

  // Onboarding state — collected after account creation
  const [onboardData, setOnboardData] = useState({
    club: '', formation: '4-3-3', style: 'possession', yearsCoaching: '',
  });
  const setOb = (k, v) => setOnboardData(prev => ({...prev, [k]: v}));

  const pwStrength = getPasswordStrength(password);

  const handleSubmit = async () => {
    setError(''); setSuccess('');
    if (!email || !password) { setError('Please fill in all required fields.'); return; }
    if (mode === 'signup') {
      if (!name) { setError('Please enter your name.'); return; }
      if (password !== confirmPw) { setError('Passwords do not match.'); return; }
      if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    }
    setLoading(true);
    try {
      const result = mode === 'login'
        ? await signIn(email, password, role)
        : await signUp(email, password, name, role);
      if (!result.success) setError(result.error);
      if (result.success) {
        if (result.userId) setSignedUpUserId(result.userId);
        if (result.message) {
          // Supabase signup — go to onboarding first, then confirm email
          setMode('onboard');
        }
        // Local signup (no message) — user is already logged in, onboarding still runs
        if (!result.message && result.success) {
          setMode('onboard');
        }
        setSignedUpEmail(email);
      }
    } catch(e) {
      setError('Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  const handleOnboardSave = () => {
    // Save onboarding data against the user ID (or 'pending' for pre-confirmation)
    const key = signedUpUserId ? `profile_${signedUpUserId}` : `profile_pending_${email}`;
    const profileData = {
      name,
      email,
      role,
      club: onboardData.club,
      formation: onboardData.formation,
      style: onboardData.style,
      yearsCoaching: onboardData.yearsCoaching,
      badge: 'none',
      kitColour: '#2e7d7a',
      onboardedAt: new Date().toISOString(),
    };
    saveToStorage(key, profileData);
    // Also save club name for fixtures/dashboard
    if (onboardData.club) saveToStorage('fx_club_name', onboardData.club);
    // Also save pending key so Dashboard can find it before userId resolves
    saveToStorage('profile_pending', profileData);

    // If Supabase signup, show email confirmation. Local signup → they're already in.
    if (signedUpEmail) {
      setMode('confirm');
    }
  };

  const fillDemo = (demo) => {
    setEmail(demo.email);
    setPassword(demo.password);
    setRole(demo.role);
    setMode('login');
  };

  // ── ONBOARDING STEP ──
  if (mode === 'onboard') {
    const formations = ["4-3-3","4-4-2","4-2-3-1","4-4-2 Diamond","3-5-2","3-4-3","4-1-4-1","4-5-1","5-4-1","4-3-1-2"];
    const styles = [
      {id:"possession",   label:"Possession",     desc:"Control through the ball"},
      {id:"high-press",   label:"High Press",     desc:"Win it back immediately"},
      {id:"counter",      label:"Counter-attack",  desc:"Fast transitions"},
      {id:"low-block",    label:"Low Block",       desc:"Compact and clinical"},
      {id:"direct",       label:"Direct",          desc:"Simple and effective"},
    ];
    return (
      <>
        <style>{AUTH_CSS}</style>
        <style>{`
          .ob-wrap { min-height:100vh; background:var(--ink); display:flex; align-items:center; justify-content:center; padding:1.5rem; position:relative; overflow:hidden; }
          .ob-wrap::before { content:''; position:absolute; inset:0; background:repeating-linear-gradient(0deg,transparent,transparent 48px,rgba(255,255,255,0.02) 48px,rgba(255,255,255,0.02) 49px); pointer-events:none; }
          .ob-card { background:var(--white); border:2.5px solid var(--ink); width:100%; max-width:520px; position:relative; z-index:1; box-shadow:8px 8px 0 rgba(0,0,0,0.4); }
          .ob-head { background:linear-gradient(135deg,var(--teal2),var(--teal)); padding:1.8rem 2rem; border-bottom:3px solid var(--mustard); }
          .ob-logo { display:flex; align-items:center; gap:0.6rem; margin-bottom:1rem; }
          .ob-logo-badge { width:32px; height:32px; background:var(--mustard); border:2px solid rgba(255,255,255,0.3); display:flex; align-items:center; justify-content:center; font-family:'Bebas Neue',sans-serif; font-size:0.9rem; color:var(--ink); transform:rotate(-3deg); }
          .ob-logo-name { font-family:'Bebas Neue',sans-serif; font-size:1.3rem; letter-spacing:3px; color:var(--white); }
          .ob-title { font-family:'Bebas Neue',sans-serif; font-size:1.8rem; letter-spacing:3px; color:var(--white); line-height:1; margin-bottom:0.35rem; }
          .ob-sub { font-family:'DM Sans',sans-serif; font-size:0.75rem; color:rgba(255,255,255,0.65); font-weight:300; line-height:1.65; }
          .ob-body { padding:1.8rem 2rem; }
          .ob-field { margin-bottom:1.1rem; }
          .ob-label { display:block; font-family:'DM Sans',sans-serif; font-size:0.6rem; font-weight:800; letter-spacing:2px; text-transform:uppercase; color:var(--muted); margin-bottom:0.38rem; }
          .ob-input { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.65rem 0.9rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.88rem; font-weight:500; outline:none; transition:border-color 0.2s; }
          .ob-input:focus { border-bottom-color:var(--teal); }
          .ob-select { width:100%; background:var(--cream); border:1.5px solid var(--rule); border-bottom:2px solid var(--ink); padding:0.65rem 0.9rem; color:var(--ink); font-family:'DM Sans',sans-serif; font-size:0.88rem; font-weight:500; outline:none; cursor:pointer; }
          .ob-style-grid { display:grid; grid-template-columns:1fr 1fr; gap:0.5rem; }
          .ob-style-btn { padding:0.6rem 0.85rem; border:1.5px solid var(--rule); background:var(--white); cursor:pointer; text-align:left; transition:all 0.18s; }
          .ob-style-btn.on { border-color:var(--teal); background:var(--teal4); }
          .ob-style-btn:hover:not(.on) { border-color:var(--ink); }
          .ob-style-name { font-family:'DM Sans',sans-serif; font-size:0.78rem; font-weight:800; color:var(--ink); }
          .ob-style-desc { font-family:'DM Sans',sans-serif; font-size:0.62rem; color:var(--muted); margin-top:0.1rem; }
          .ob-skip { font-family:'DM Sans',sans-serif; font-size:0.7rem; color:var(--muted); cursor:pointer; text-align:center; text-decoration:underline; margin-top:0.75rem; display:block; }
          .ob-skip:hover { color:var(--teal); }
          .ob-submit { width:100%; padding:0.9rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.82rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; }
          .ob-submit:hover { background:var(--teal2); }
          .ob-welcome { font-family:'DM Sans',sans-serif; font-size:0.82rem; color:var(--muted); font-style:italic; text-align:center; margin-bottom:1.2rem; }
        `}</style>
        <div className="ob-wrap">
          <div className="auth-corner tl"/><div className="auth-corner tr"/>
          <div className="auth-corner bl"/><div className="auth-corner br"/>
          <div className="ob-card">
            <div className="ob-head">
              <div className="ob-logo">
                <div className="ob-logo-badge">FA</div>
                <div className="ob-logo-name">Footy Analyst</div>
              </div>
              <div className="ob-title">Welcome, {name.split(' ')[0]}.</div>
              <div className="ob-sub">Tell us about your club so your dashboard is ready when you sign in. Takes 30 seconds.</div>
            </div>
            <div className="ob-body">
              <div className="ob-welcome">Account created ✓</div>

              <div className="ob-field">
                <label className="ob-label">Club or Team Name *</label>
                <input className="ob-input" placeholder="e.g. Northgate FC, Town Academy U18" value={onboardData.club} onChange={e=>setOb('club',e.target.value)} autoFocus/>
              </div>

              <div className="ob-field">
                <label className="ob-label">Your Preferred Formation</label>
                <select className="ob-select" value={onboardData.formation} onChange={e=>setOb('formation',e.target.value)}>
                  {formations.map(f=><option key={f} value={f}>{f}</option>)}
                </select>
              </div>

              <div className="ob-field">
                <label className="ob-label">Playing Style</label>
                <div className="ob-style-grid">
                  {styles.map(s=>(
                    <button key={s.id} className={`ob-style-btn${onboardData.style===s.id?" on":""}`} onClick={()=>setOb('style',s.id)}>
                      <div className="ob-style-name">{s.label}</div>
                      <div className="ob-style-desc">{s.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="ob-field">
                <label className="ob-label">Years Coaching <span style={{fontWeight:400,letterSpacing:0,textTransform:'none',fontStyle:'italic'}}> — optional</span></label>
                <select className="ob-select" value={onboardData.yearsCoaching} onChange={e=>setOb('yearsCoaching',e.target.value)}>
                  <option value="">Select…</option>
                  {["Less than 1 year","1–2 years","3–5 years","5–10 years","10+ years"].map(y=><option key={y} value={y}>{y}</option>)}
                </select>
              </div>

              <button className="ob-submit" onClick={handleOnboardSave} disabled={!onboardData.club}>
                {onboardData.club ? "Set Up My Dashboard →" : "Enter your club name to continue"}
              </button>
              <span className="ob-skip" onClick={handleOnboardSave}>Skip for now — I'll fill this in later</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── EMAIL CONFIRMATION SCREEN ──
  if (mode === 'confirm') {
    return (
      <>
        <style>{AUTH_CSS}</style>
        <style>{`
          .confirm-wrap { min-height:100vh; background:var(--teal2); display:flex; align-items:center; justify-content:center; padding:2rem; position:relative; overflow:hidden; }
          .confirm-wrap::before { content:''; position:absolute; inset:0; background:repeating-linear-gradient(0deg,transparent,transparent 48px,rgba(0,0,0,0.06) 48px,rgba(0,0,0,0.06) 49px); pointer-events:none; }
          .confirm-card { background:var(--white); border:2.5px solid var(--ink); padding:2.8rem; width:100%; max-width:460px; position:relative; z-index:1; box-shadow:8px 8px 0 rgba(0,0,0,0.25); text-align:center; }
          .confirm-card::before { content:''; position:absolute; top:8px; left:8px; right:8px; bottom:8px; border:1px solid var(--rule); pointer-events:none; }
          .confirm-icon { font-size:3.5rem; margin-bottom:1.2rem; display:block; }
          .confirm-title { font-family:'Bebas Neue',sans-serif; font-size:2rem; letter-spacing:3px; color:var(--ink); margin-bottom:0.5rem; }
          .confirm-sub { font-size:0.82rem; color:var(--muted); font-weight:300; line-height:1.75; margin-bottom:1.5rem; }
          .confirm-email-box { background:var(--teal4); border:1.5px solid var(--teal); padding:0.75rem 1rem; margin-bottom:1.5rem; }
          .confirm-email { font-family:'DM Sans',sans-serif; font-size:0.92rem; font-weight:800; color:var(--teal2); }
          .confirm-steps { text-align:left; margin-bottom:1.8rem; }
          .confirm-step { display:flex; align-items:flex-start; gap:0.75rem; padding:0.6rem 0; border-bottom:1px solid var(--rule2); }
          .confirm-step:last-child { border-bottom:none; }
          .confirm-step-num { width:24px; height:24px; background:var(--teal); color:var(--white); border-radius:50%; display:flex; align-items:center; justify-content:center; font-family:'Bebas Neue',sans-serif; font-size:0.85rem; letter-spacing:1px; flex-shrink:0; }
          .confirm-step-text { font-size:0.8rem; color:var(--ink2); font-weight:400; line-height:1.55; padding-top:0.15rem; }
          .confirm-step-text strong { font-weight:800; color:var(--ink); }
          .confirm-btn { width:100%; padding:0.85rem; background:var(--teal); color:var(--white); border:2px solid var(--teal); font-family:'DM Sans',sans-serif; font-weight:800; font-size:0.8rem; letter-spacing:2px; text-transform:uppercase; cursor:pointer; transition:all 0.2s; margin-bottom:0.65rem; }
          .confirm-btn:hover { background:var(--teal2); }
          .confirm-back { font-family:'DM Sans',sans-serif; font-size:0.75rem; color:var(--muted); cursor:pointer; text-decoration:underline; }
          .confirm-back:hover { color:var(--teal); }
          .confirm-spam-note { font-size:0.68rem; color:var(--muted); font-style:italic; margin-top:0.9rem; line-height:1.6; }
          .confirm-logo { display:flex; align-items:center; justify-content:center; gap:0.6rem; margin-bottom:1.5rem; }
          .confirm-logo-badge { width:34px; height:34px; background:var(--teal); border:2px solid var(--ink); display:flex; align-items:center; justify-content:center; font-family:'Bebas Neue',sans-serif; font-size:0.95rem; color:var(--white); transform:rotate(-3deg); }
          .confirm-logo-name { font-family:'Bebas Neue',sans-serif; font-size:1.5rem; letter-spacing:3px; color:var(--ink); }
        `}</style>
        <div className="confirm-wrap">
          <div className="auth-corner tl"/><div className="auth-corner tr"/>
          <div className="auth-corner bl"/><div className="auth-corner br"/>
          <div className="confirm-card">
            <div className="confirm-logo">
              <div className="confirm-logo-badge">FA</div>
              <div className="confirm-logo-name">Footy Analyst</div>
            </div>

            <span className="confirm-icon">📧</span>
            <div className="confirm-title">Check Your Email</div>
            <div className="confirm-sub">
              We've sent a confirmation link to your email address. Click it to activate your account and get started.
            </div>

            <div className="confirm-email-box">
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.25rem"}}>Sent to</div>
              <div className="confirm-email">{signedUpEmail}</div>
            </div>

            <div className="confirm-steps">
              <div className="confirm-step">
                <div className="confirm-step-num">1</div>
                <div className="confirm-step-text">Open your email inbox and look for a message from <strong>Footy Analyst</strong></div>
              </div>
              <div className="confirm-step">
                <div className="confirm-step-num">2</div>
                <div className="confirm-step-text">Click the <strong>"Confirm your email"</strong> link inside the email</div>
              </div>
              <div className="confirm-step">
                <div className="confirm-step-num">3</div>
                <div className="confirm-step-text">You'll be redirected back here — then <strong>sign in with your email and password</strong></div>
              </div>
            </div>

            <button className="confirm-btn" onClick={()=>setMode('login')}>
              I've Confirmed — Sign In →
            </button>

            <div className="confirm-spam-note">
              Can't find the email? Check your spam or junk folder.<br/>
              The link expires after 24 hours.
            </div>

            <div style={{marginTop:"1.2rem"}}>
              <span className="confirm-back" onClick={()=>setMode('signup')}>
                ← Wrong email? Go back and try again
              </span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{AUTH_CSS}</style>
      <div className="auth-wrap">
        <div className="auth-corner tl"/><div className="auth-corner tr"/>
        <div className="auth-corner bl"/><div className="auth-corner br"/>

        <div className="auth-card">
          {/* Logo */}
          <div className="auth-logo">
            <div className="auth-badge">FA</div>
            <div className="auth-brand">Footy Analyst</div>
          </div>
          <div className="auth-tagline">Football Intelligence Platform · footyanalyst.co.uk</div>

          {/* Login / Signup tabs */}
          <div className="auth-mode-tabs">
            <div className={`auth-mode-tab ${mode==='login'?'on':''}`} onClick={()=>{setMode('login');setError('');setSuccess('');}}>Sign In</div>
            <div className={`auth-mode-tab ${mode==='signup'?'on':''}`} onClick={()=>{setMode('signup');setError('');setSuccess('');}}>Create Account</div>
          </div>

          {/* Role */}
          <label className="auth-role-label">I am a</label>
          <div className="auth-roles">
            <button className={`auth-role-btn ${role==='coach'?'on':''}`} onClick={()=>setRole('coach')}>🎯 Coach</button>
            <button className={`auth-role-btn ${role==='scout'?'on':''}`} onClick={()=>setRole('scout')}>🔍 Scout</button>
            <button className={`auth-role-btn ${role==='analyst'?'on':''}`} onClick={()=>setRole('analyst')}>📊 Analyst</button>
          </div>

          {/* Name field (signup only) */}
          {mode==='signup'&&(
            <div className="auth-field">
              <label>Full Name</label>
              <input className={`auth-input ${error&&!name?'error':''}`} placeholder="e.g. James Clarke" value={name} onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSubmit()}/>
            </div>
          )}

          {/* Email */}
          <div className="auth-field">
            <label>Email Address</label>
            <input className={`auth-input ${error&&!email?'error':''}`} type="email" placeholder="you@yourclub.com" value={email} onChange={e=>setEmail(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSubmit()}/>
          </div>

          {/* Password */}
          <div className="auth-field">
            <label>Password</label>
            <input className={`auth-input ${error&&!password?'error':''}`} type="password" placeholder={mode==='login'?'Your password':'Min. 8 characters'} value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSubmit()}/>
            {mode==='signup'&&password&&(
              <div className="auth-strength">
                <div className="auth-strength-bar" style={{width:`${(pwStrength.score/5)*100}%`,background:pwStrength.color}}/>
                <div className="auth-strength-label" style={{color:pwStrength.color}}>{pwStrength.label}</div>
              </div>
            )}
          </div>

          {/* Confirm password (signup only) */}
          {mode==='signup'&&(
            <div className="auth-field">
              <label>Confirm Password</label>
              <input className={`auth-input ${confirmPw&&confirmPw!==password?'error':''}`} type="password" placeholder="Repeat your password" value={confirmPw} onChange={e=>setConfirmPw(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleSubmit()}/>
            </div>
          )}

          {/* Error */}
          {error&&<div className="auth-error">⚠ {error}</div>}

          {/* Submit */}
          <button className="auth-submit" onClick={handleSubmit} disabled={loading}>
            {loading&&<div className="auth-submit-spinner"/>}
            {loading?'Please wait...':(mode==='login'?`Sign In as ${role==='coach'?'Coach':role==='scout'?'Scout':'Analyst'}`:'Create My Account')}
          </button>

          {/* Switch mode */}
          <div className="auth-switch">
            {mode==='login'
              ?<>Don't have an account? <span onClick={()=>{setMode('signup');setError('');setSuccess('');}}>Create one free</span></>
              :(<>Already have an account? <span onClick={()=>{setMode('login');setError('');setSuccess('');}}>Sign in</span></>)
            }
          </div>
        </div>
      </div>
    </>
  );
}

/* ══════════════════════════════════════
   USER PILL — shown in nav when logged in
══════════════════════════════════════ */
export function UserPill({ onSignOut, onNavigate }) {
  const { user } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  if (!user) return null;

  const go = (tab) => { setShowMenu(false); if(onNavigate) onNavigate(tab); };

  return (
    <div style={{position:'relative'}}>
      <div className="user-pill" style={{cursor:'pointer'}} onClick={()=>setShowMenu(m=>!m)}>
        <div className="user-pill-av" style={{background: loadFromStorage(`profile_${user.id}`,{})?.kitColour||'var(--darkred)'}}>
          {user.initials||user.name?.slice(0,2).toUpperCase()||'FA'}
        </div>
        <div>
          <div className="user-pill-name">{user.name||user.email}</div>
          <div className="user-pill-role">{user.role} · {user.plan}</div>
        </div>
        <span style={{color:'var(--muted)',fontSize:'0.65rem',marginLeft:'0.3rem'}}>▾</span>
      </div>
      {showMenu&&(
        <div style={{position:'absolute',top:'calc(100% + 4px)',right:0,background:'var(--white)',border:'1.5px solid var(--ink)',minWidth:'190px',zIndex:600,boxShadow:'4px 4px 0 rgba(0,0,0,0.15)'}}>
          <div style={{padding:'0.75rem 1rem',borderBottom:'1px solid var(--rule)',background:'var(--cream2)'}}>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:'0.82rem',fontWeight:800,color:'var(--ink)'}}>{user.name}</div>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:'0.62rem',color:'var(--muted)',marginTop:'0.1rem'}}>{user.email}</div>
            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:'0.56rem',fontWeight:800,letterSpacing:'2px',textTransform:'uppercase',color:'var(--teal)',marginTop:'0.3rem'}}>{user.plan||"Pro"} Plan</div>
          </div>
          <div style={{padding:'0.4rem 0'}}>
            {[
              {icon:'👤', label:'My Profile',    tab:'profile'},
              {icon:'⚽', label:'Fixtures',       tab:'fixtures'},
              {icon:'✦',  label:'AI Analyst',     tab:'ai'},
            ].map(item=>(
              <div key={item.label}
                style={{display:'flex',alignItems:'center',gap:'0.6rem',padding:'0.48rem 1rem',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",fontSize:'0.78rem',fontWeight:600,color:'var(--ink)',transition:'all 0.18s'}}
                onMouseEnter={e=>e.currentTarget.style.background='var(--teal4)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                onClick={()=>go(item.tab)}>
                <span>{item.icon}</span>{item.label}
              </div>
            ))}
            <div style={{height:'1px',background:'var(--rule)',margin:'0.3rem 0'}}/>
            <div
              style={{display:'flex',alignItems:'center',gap:'0.6rem',padding:'0.48rem 1rem',cursor:'pointer',fontFamily:"'DM Sans',sans-serif",fontSize:'0.78rem',fontWeight:700,color:'var(--darkred)',transition:'all 0.18s'}}
              onMouseEnter={e=>e.currentTarget.style.background='var(--darkred3)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}
              onClick={()=>{setShowMenu(false);onSignOut();}}>
              <span>🚪</span>Sign Out
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SESSION PLANNER HELPERS ── */
export function addDrillToSession(drill) {
  const current = loadFromStorage('session_drills', []);
  // Avoid duplicates
  if (current.find(d => d.title === drill.title)) return false;
  const updated = [...current, { ...drill, addedAt: new Date().toISOString(), id: Date.now() }];
  saveToStorage('session_drills', updated);
  return true;
}

export function getSessionDrills() {
  return loadFromStorage('session_drills', []);
}

export function removeDrillFromSession(id) {
  const updated = loadFromStorage('session_drills', []).filter(d => d.id !== id);
  saveToStorage('session_drills', updated);
  return updated;
}

export function clearSessionDrills() {
  saveToStorage('session_drills', []);
}

/* ══════════════════════════════════════════
   GLOBAL TOAST NOTIFICATION SYSTEM
   Usage: const toast = useToast();
          toast("Player saved!", "success");
          toast("Error saving", "error");
          toast("Syncing...", "info");
══════════════════════════════════════════ */

const ToastContext = createContext(null);

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const counterRef = useRef(0);

  const showToast = useCallback((message, type = "success", duration = 2200) => {
    const id = ++counterRef.current;
    setToasts(prev => [...prev.slice(-3), { id, message, type }]); // max 4 at once
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  }, []);

  const ICON = { success: "✓", error: "✕", info: "✦", warning: "⚠" };
  const COLOR = {
    success: { bg:"#065f46", border:"#34d399", icon:"#34d399" },
    error:   { bg:"#7f1d1d", border:"#f87171", icon:"#f87171" },
    info:    { bg:"#1a1612", border:"#2e7d7a", icon:"#2e7d7a" },
    warning: { bg:"#78350f", border:"#fbbf24", icon:"#fbbf24" },
  };

  return (
    <ToastContext.Provider value={showToast}>
      {children}
      {toasts.length > 0 && (
        <div style={{
          position:"fixed", bottom:"1.2rem", right:"1.2rem",
          zIndex:9999, display:"flex", flexDirection:"column", gap:"0.5rem",
          pointerEvents:"none",
        }}>
          {toasts.map(t => {
            const c = COLOR[t.type] || COLOR.success;
            return (
              <div key={t.id} style={{
                display:"flex", alignItems:"center", gap:"0.6rem",
                background:c.bg, border:`1.5px solid ${c.border}`,
                padding:"0.65rem 1rem", minWidth:220, maxWidth:320,
                boxShadow:"0 4px 16px rgba(0,0,0,0.4)",
                animation:"toastIn 0.2s ease",
              }}>
                <span style={{color:c.icon,fontWeight:800,fontSize:"0.85rem",flexShrink:0}}>{ICON[t.type]}</span>
                <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:600,color:"#fff",lineHeight:1.4}}>{t.message}</span>
              </div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes toastIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </ToastContext.Provider>
  );
}
